import { initializeApp } from 'firebase/app';
import {
  type MessagePayload,
  type Messaging,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';

import { hasStringValue } from '@core/utils/equality';

import { SlabConfig } from '../utils/SlabConfig';

export { getToken, onMessage };
export type { MessagePayload };
const MESSAGING_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

type PushConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  vapidKey: string;
};

const readPushConfig = (): PushConfig | null => {
  const { apiKey, authDomain, projectId, storageBucket, messagingSenderId, appId, vapidKey } =
    SlabConfig.firebase ?? {};

  if (
    !hasStringValue(apiKey) ||
    !hasStringValue(authDomain) ||
    !hasStringValue(projectId) ||
    !hasStringValue(storageBucket) ||
    !hasStringValue(messagingSenderId) ||
    !hasStringValue(appId) ||
    !hasStringValue(vapidKey)
  ) {
    return null;
  }

  return { apiKey, authDomain, projectId, storageBucket, messagingSenderId, appId, vapidKey };
};

export const pushConfig = readPushConfig();

const serviceWorkerUrl = (config: PushConfig): string => {
  const params = new URLSearchParams({
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    storageBucket: config.storageBucket,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId,
  });

  return `/firebase-messaging-sw.js?${params.toString()}`;
};

export const isPushSupported = async (): Promise<boolean> => {
  if (pushConfig === null) {
    return false;
  }

  if (
    !('Notification' in window) ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    return false;
  }

  return isSupported();
};

let messagingClient: Messaging | null = null;

export const getMessagingClient = (): Messaging | null => {
  if (pushConfig === null) {
    return null;
  }

  if (messagingClient === null) {
    messagingClient = getMessaging(initializeApp(pushConfig));
  }

  return messagingClient;
};

let serviceWorkerRegistration: Promise<ServiceWorkerRegistration> | null = null;

export const registerMessagingServiceWorker =
  async (): Promise<ServiceWorkerRegistration | null> => {
    if (pushConfig === null) {
      return null;
    }

    if (serviceWorkerRegistration === null) {
      serviceWorkerRegistration = navigator.serviceWorker
        .register(serviceWorkerUrl(pushConfig), { scope: MESSAGING_SW_SCOPE })
        .catch((error) => {
          serviceWorkerRegistration = null;
          throw error;
        });
    }

    return serviceWorkerRegistration;
  };
